import React, { useEffect, useState } from "react";
import axios from "axios";
import "./App.css";
import { NavLink, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import LandingPage from "./pages/LandingPage";
import HomePage from "./pages/HomePage";
import MovieDetailsPage from "./pages/MovieDetailsPage";
import ProgressPage from "./pages/ProgressPage";
import WeeklyReportDetailPage from "./pages/WeeklyReportDetailPage";
import BoxOfficeDataPage from "./pages/BoxOfficeDataPage";
import BoxOfficeMainPage from "./pages/BoxOfficeMainPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import ChatPage from "./pages/ChatPage";
import FirebaseTestPage from "./pages/FirebaseTestPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import SettingsPage from "./pages/SettingsPage";
import ProfilePage from "./pages/ProfilePage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsPage from "./pages/TermsPage";
import ContactPage from "./pages/ContactPage";
import Footer from "./components/Footer";

import { MdHome, MdMovie, MdBarChart, MdAttachMoney, MdEmojiEvents, MdChat, MdSettings, MdLogout, MdPerson, MdArrowDropDown, MdLocalFireDepartment, MdArticle } from "react-icons/md";
import { decodeId } from "./utils/idObfuscation";
import RewardPopup from "./components/RewardPopup";

const API = process.env.REACT_APP_API_URL || '';


function App() {
  const [movies, setMovies] = useState([]);
  const [tab, setTab] = useState("upcoming"); // 'upcoming' or 'old'
  const [languageFilter, setLanguageFilter] = useState('telugu'); // null = all, 'english', 'telugu', 'tamil'
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMovies, setLoadingMovies] = useState(false);
  const [topMovies, setTopMovies] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const [token, setToken] = useState(localStorage.getItem("token") || null);
  const [user, setUser] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [authForm, setAuthForm] = useState({ name: "", email: "", password: "" });
  const [votePoints, setVotePoints] = useState({});
  const [error, setError] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidenavCollapsed, setSidenavCollapsed] = useState(false);
  const [boxOfficeExpanded, setBoxOfficeExpanded] = useState(false);
  const [reward, setReward] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem("darkMode");
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    // Apply dark mode class to document root
    if (darkMode) {
      document.documentElement.classList.add("dark-mode");
    } else {
      document.documentElement.classList.remove("dark-mode");
    }
    localStorage.setItem("darkMode", JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    // fetch first page when token or tab changes
    setPage(1);
    setHasMore(true);
    fetchMovies(1, tab, false, languageFilter);
    fetchTopMovies();
    if (token) fetchMe(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, tab]);

  useEffect(() => {
    // Reload movies when language filter changes (only for upcoming tab)
    if (tab === 'upcoming') {
      setPage(1);
      setHasMore(true);
      fetchMovies(1, tab, false, languageFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languageFilter]);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (showMenu && !event.target.closest('.user-dropdown')) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showMenu]);

  // Auto-expand box office menu if on box office route
  useEffect(() => {
    if (location.pathname.startsWith('/boxoffice')) {
      setBoxOfficeExpanded(true);
    }
  }, [location.pathname]);

  const fetchMovies = async (pageArg = 1, tabArg = tab, append = false, language = null) => {
    try {
      setLoadingMovies(true);
      const params = { page: pageArg, limit: 20, tab: tabArg };
      // Only add language filter for upcoming tab
      if (tabArg === 'upcoming' && language) {
        params.language = language;
      }
      const res = await axios.get(`${API}/api/movies`, { 
        params: { ...params, _t: Date.now() } // Add cache-busting parameter
      });
      const data = res.data;
      // support both old-style array responses and new { movies, total } responses
      const moviesFromServer = Array.isArray(data) ? data : (data.movies || []);
      const total = Array.isArray(data) ? moviesFromServer.length : (data.total || moviesFromServer.length);
      if (append) setMovies(prev => [...prev, ...moviesFromServer]);
      else setMovies(moviesFromServer);
      // update hasMore based on total
      const currentCount = (append ? movies.length : moviesFromServer.length);
      setHasMore(currentCount < (total || 0));
      setError("");
    } catch (e) {
      setError("Failed to load movies: " + (e.response?.data?.error || e.message || "Unknown error"));
    } finally {
      setLoadingMovies(false);
    }
  };

  const fetchTopMovies = async () => {
    try {
      const res = await axios.get(`${API}/api/charts`, {
        params: { _t: Date.now() } // Add cache-busting parameter
      });
      setTopMovies(res.data);
    } catch (e) {
      // Error fetching top movies
    }
  };

  const fetchMe = async (tkn) => {
    try {
      // Add timestamp to prevent browser caching
      const res = await axios.get(`${API}/api/me`, { 
        headers: { Authorization: `Bearer ${tkn}` },
        params: { _t: Date.now() }
      });
      setUser(res.data);
      
      // Check for new reward
      if (res.data.newReward) {
        setReward(res.data.newReward);
      }
    } catch (e) {
      // invalid token - clear user state
      setUser(null);
      setToken(null);
      localStorage.removeItem("token");
    }
  };

  // Auth handlers
  const handleAuthChange = (e) => {
    setAuthForm({ ...authForm, [e.target.name]: e.target.value });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    try {
      // Clear old user data first
      setUser(null);
      
      const res = await axios.post(`${API}/api/register`, {
        name: authForm.name,
        email: authForm.email,
        password: authForm.password,
      });
      const tkn = res.data.token;
      setToken(tkn);
      localStorage.setItem("token", tkn);
      // Fetch fresh user data to ensure it's current
      const userRes = await axios.get(`${API}/api/me`, { 
        headers: { Authorization: `Bearer ${tkn}` },
        params: { _t: Date.now() }
      });
      setUser(userRes.data);
      
      // Check for new reward
      if (userRes.data.newReward) {
        setReward(userRes.data.newReward);
      }
      setAuthForm({ name: "", email: "", password: "" });
      navigate("/");
    } catch (err) {
      setError(err.response?.data?.error || "Registration failed");
      // Clear token if registration fails
      setToken(null);
      localStorage.removeItem("token");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    try {
      // Clear old user data first
      setUser(null);
      
      const res = await axios.post(`${API}/api/login`, {
        email: authForm.email,
        password: authForm.password,
      });
      const tkn = res.data.token;
      setToken(tkn);
      localStorage.setItem("token", tkn);
      // Fetch fresh user data to ensure it's current
      const userRes = await axios.get(`${API}/api/me`, { 
        headers: { Authorization: `Bearer ${tkn}` },
        params: { _t: Date.now() }
      });
      setUser(userRes.data);
      
      // Check for new reward
      if (userRes.data.newReward) {
        setReward(userRes.data.newReward);
      }
      setAuthForm({ name: "", email: "", password: "" });
      navigate("/");
    } catch (err) {
      setError(err.response?.data?.error || "Login failed");
      // Clear token if login fails
      setToken(null);
      localStorage.removeItem("token");
    }
  };

  const handleLogout = async () => {
    // Sign out from Firebase if initialized
    try {
      const { auth } = await import('./config/firebase');
      if (auth?.currentUser) {
        const { signOut } = await import('firebase/auth');
        await signOut(auth);
      }
    } catch (err) {
      // Firebase might not be initialized, ignore
    }
    
    // Clear all user-related state
    setUser(null);
    setToken(null);
    localStorage.removeItem("token");
    // Clear vote points cache
    setVotePoints({});
    // Redirect to landing page if on settings page
    if (window.location.pathname === "/settings") {
      navigate("/");
    }
  };

  const switchToRegister = () => navigate("/register");
  const switchToLogin = () => navigate("/login");

  // Wrapper function to pass points directly to handleVote
  const voteWithPoints = (movieId, points) => {
    return handleVote(movieId, points);
  };

  const handleVote = async (movieId, pointsOverride = null) => {
    setError("");
    // movieId from frontend is encoded, decode it before sending to API
    const realMovieId = decodeId(movieId);
    if (!realMovieId) {
      setError("Invalid movie ID");
      return null;
    }
    // Use pointsOverride if provided (from slider), otherwise use votePoints state
    const points = pointsOverride !== null ? pointsOverride : parseInt(votePoints[movieId] || "0", 10);
    if (points === 0) {
      setError("Please select at least 1 point to vote");
      return null;
    }
    if (!token) {
      setError("You must be logged in to vote");
      return null;
    }
    try {
      const res = await axios.post(
        `${API}/api/vote`,
        { movieId: realMovieId, points },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update only the specific movie in the list (optimization: no full refetch)
      if (res.data.movie) {
        setMovies(prevMovies => 
          prevMovies.map(m => 
            m.id === movieId 
              ? { ...m, totalPoints: res.data.movie.totalPoints }
              : m
          )
        );
        
        // Update topMovies if this movie is in the top 5
        setTopMovies(prevTop => {
          const movieInTop = prevTop.find(m => m.id === movieId);
          if (movieInTop) {
            // Movie is in top 5, update it and re-sort
            const updated = prevTop.map(m => 
              m.id === movieId 
                ? { ...m, totalPoints: res.data.movie.totalPoints }
                : m
            );
            return updated.sort((a, b) => b.totalPoints - a.totalPoints);
          }
          return prevTop;
        });
      }
      
      // Update user data from response - always update to ensure points are current
      if (res.data.user) {
        setUser(res.data.user);
        
        // Check for new reward earned during voting
        if (res.data.user.newReward) {
          setReward(res.data.user.newReward);
        }
      } else {
        // If user data not in response, fetch it to ensure header is up to date
        fetchMe(token);
      }
      
      setVotePoints({ ...votePoints, [movieId]: "1" });
      
      // Return response data so callers (like MovieDetailsPage) can use it
      return res.data;
    } catch (err) {
      setError(err.response?.data?.error || "Vote failed");
      return null;
    }
  };

  const updateVotePoints = (movieId, val) => {
    // Don't cap at 10 - allow up to user's available points
    const num = Math.max(0, Number(val || 0));
    setVotePoints({ ...votePoints, [movieId]: String(num) });
  };

  //const location = useLocation();

  // Notify AdSense on route changes (for React Router)
  useEffect(() => {
    // Small delay to ensure DOM is updated
    const timer = setTimeout(() => {
      if (window.adsbygoogle) {
        try {
          window.adsbygoogle.push({});
        } catch (e) {
          // Ignore errors in dev
        }
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Close Box Office submenu when clicking outside (only when collapsed)
  useEffect(() => {
    if (!sidenavCollapsed || !boxOfficeExpanded) return;

    const handleClickOutside = (event) => {
      const sidenav = document.querySelector('.sidenav');
      const submenu = document.querySelector('.sidelink-submenu');
      
      if (sidenav && submenu && !sidenav.contains(event.target)) {
        setBoxOfficeExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sidenavCollapsed, boxOfficeExpanded]);


  // Disabled infinite scroll - using manual Load More button instead
  // useEffect(() => {
  //   const onScroll = () => {
  //     if (loadingMovies || !hasMore) return;
  //     if ((window.innerHeight + window.scrollY) >= (document.body.offsetHeight - 300)) {
  //       setPage(p => {
  //         const next = p + 1;
  //         fetchMovies(next, tab, true);
  //         return next;
  //       });
  //     }
  //   };
  //   window.addEventListener("scroll", onScroll);
  //   return () => window.removeEventListener("scroll", onScroll);
  // }, [loadingMovies, hasMore, tab]);


  return (
    <div className="app-shell">
      <header className="site-header">
        <div className="site-brand">
          <button
            className="hamburger"
            onClick={() => {
              // on large screens toggle collapsed mini-sidenav, on small screens open overlay
              if (window.innerWidth > 900) setSidenavCollapsed(s => !s);
              else setSidebarOpen(s => !s);
            }}
            aria-label="Toggle menu"
          >
            ☰
          </button>
          TollyHype
        </div>
        <div className="header-right">
          {user ? (
            <>
            <div className="user-dropdown">
              <button 
                className="theme-toggle" 
                onClick={() => setDarkMode(!darkMode)}
                title={darkMode ? "Light mode" : "Dark mode"}
                aria-label="Toggle dark mode"
              >
                {darkMode ? "☀️" : "🌙"}
              </button>
              <div 
                className={`streak-display ${(user.currentStreak || 0) > 0 ? 'active' : ''}`}
                onClick={() => navigate("/profile")}
                data-streak-text={`${user.currentStreak || 0} ${(user.currentStreak || 0) === 1 ? 'streak' : 'streaks'}`}
                title={`${user.currentStreak || 0} ${(user.currentStreak || 0) === 1 ? 'streak' : 'streaks'}`}
              >
                <MdLocalFireDepartment />
                <span>{user.currentStreak || 0}</span>
              </div>
              <div className="points-display">
                <span className="points-value">{user.pointsLeftToday}</span>
                <span className="points-label">pts</span>
              </div>
              <button className="user-name clickable" onClick={() => setShowMenu(!showMenu)}>
                <span className="user-info">
                  <span className="user-name-text">{user.name}</span>
                  {user.levelInfo && (
                    <span className="user-level-badge">Level {user.levelInfo.level}</span>
                  )}
                </span>
                <MdArrowDropDown className={`dropdown-arrow ${showMenu ? 'open' : ''}`} />
              </button>
              
              {showMenu && (
                <div className="dropdown-menu">
                  <NavLink to="/profile" onClick={() => setShowMenu(false)}>
                    <MdPerson />
                    <span>Profile</span>
                  </NavLink>
                  <NavLink to="/settings" onClick={() => setShowMenu(false)}>
                    <MdSettings />
                    <span>Settings</span>
                  </NavLink>
                  <button onClick={() => { setShowMenu(false); handleLogout(); }}>
                    <MdLogout />
                    <span>Logout</span>
                  </button>
                </div>
              )}
              </div>
              {/* <button className="small" onClick={handleLogout}>Logout</button> */}
            </>
          ) : (
            <div style={{ display: "flex", gap: 8 }}>
              <button 
                className="theme-toggle" 
                onClick={() => setDarkMode(!darkMode)}
                title={darkMode ? "Light mode" : "Dark mode"}
                aria-label="Toggle dark mode"
              >
                {darkMode ? "☀️" : "🌙"}
              </button>
              <NavLink to="/login" className={({ isActive }) => `tab-button ${isActive ? "active" : ""}`}>Login</NavLink>
              <NavLink to="/register" className={({ isActive }) => `tab-button ${isActive ? "active" : ""}`}>Register</NavLink>
            </div>
          )}
        </div>
      </header>

      <div className="layout">
        <aside className={`sidenav ${sidebarOpen ? 'open' : ''} ${sidenavCollapsed ? 'collapsed' : ''}`}>
          <nav>
            <ul>
              <li>
                <NavLink to="/" end className={({ isActive }) => isActive ? 'sidelink active' : 'sidelink'}>
                  <span className="sidelink-icon"><MdHome /></span>
                  <span className="sidelink-label">Home</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/movies" className={({ isActive }) => isActive ? 'sidelink active' : 'sidelink'}>
                  <span className="sidelink-icon"><MdMovie /></span>
                  <span className="sidelink-label">Movies</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/progress" className={({ isActive }) => isActive ? 'sidelink active' : 'sidelink'}>
                  <span className="sidelink-icon"><MdBarChart /></span>
                  <span className="sidelink-label">Progress</span>
                </NavLink>
              </li>
              <li>
                <button
                  className={`sidelink ${location.pathname.startsWith('/boxoffice') ? 'active' : ''}`}
                  onClick={() => setBoxOfficeExpanded(!boxOfficeExpanded)}
                  style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  <span className="sidelink-icon"><MdAttachMoney /></span>
                  <span className="sidelink-label">Box Office</span>
                  {!sidenavCollapsed && (
                    <MdArrowDropDown className={`sidelink-arrow ${boxOfficeExpanded ? 'open' : ''}`} style={{ marginLeft: 'auto' }} />
                  )}
                </button>
                {boxOfficeExpanded && (
                  <ul className="sidelink-submenu">
                    <li>
                      <NavLink 
                        to="/boxoffice/report" 
                        className={({ isActive }) => `sidelink-subitem ${isActive ? 'active' : ''}`}
                        onClick={() => {
                          if (window.innerWidth <= 900) setSidebarOpen(false);
                          if (sidenavCollapsed) setBoxOfficeExpanded(false);
                        }}
                      >
                        <span className="sidelink-icon"><MdArticle /></span>
                        <span className="sidelink-label">Weekly Reports</span>
                      </NavLink>
                    </li>
                    <li>
                      <NavLink 
                        to="/boxoffice/data" 
                        className={({ isActive }) => `sidelink-subitem ${isActive ? 'active' : ''}`}
                        onClick={() => {
                          if (window.innerWidth <= 900) setSidebarOpen(false);
                          if (sidenavCollapsed) setBoxOfficeExpanded(false);
                        }}
                      >
                        <span className="sidelink-icon"><MdBarChart /></span>
                        <span className="sidelink-label">Box Office Data</span>
                      </NavLink>
                    </li>
                  </ul>
                )}
              </li>
              <li>
                <NavLink to="/leaderboard" className={({ isActive }) => isActive ? 'sidelink active' : 'sidelink'}>
                  <span className="sidelink-icon"><MdEmojiEvents /></span>
                  <span className="sidelink-label">Leaderboard</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/chat" className={({ isActive }) => isActive ? 'sidelink active' : 'sidelink'}>
                  <span className="sidelink-icon"><MdChat /></span>
                  <span className="sidelink-label">Chat</span>
                </NavLink>
              </li>
            </ul>
          </nav>
        </aside>
        {sidebarOpen && <div className="overlay" onClick={() => setSidebarOpen(false)} />}

        <main className="main-content" onClick={() => { if (sidebarOpen) setSidebarOpen(false); }}>
          {error && <div className="error">{error}</div>}

          <Routes>
            <Route path="/" element={<LandingPage topMovies={topMovies} user={user} />} />
            <Route path="/movies" element={
              <HomePage
                movies={movies}
                onVote={handleVote}
                votePoints={votePoints}
                updateVotePoints={updateVotePoints}
                user={user}
                tab={tab}
                setTab={setTab}
                languageFilter={languageFilter}
                setLanguageFilter={setLanguageFilter}
                loadingMovies={loadingMovies}
                hasMore={hasMore}
                setPage={setPage}
                fetchMovies={fetchMovies}
                page={page}
              />
            } />
            <Route path="/movie/:movieSlug" element={
              <MovieDetailsPage 
                user={user}
                onVote={handleVote}
                votePoints={votePoints}
                updateVotePoints={updateVotePoints}
                voteWithPoints={voteWithPoints}
              />
            } />
            <Route path="/progress" element={<ProgressPage />} />
            <Route path="/boxoffice" element={<BoxOfficeMainPage user={user} />} />
            <Route path="/boxoffice/data" element={<BoxOfficeDataPage />} />
            <Route path="/boxoffice/report" element={<WeeklyReportDetailPage user={user} />} />
            <Route path="/boxoffice/report/:slug" element={<WeeklyReportDetailPage user={user} />} />
            <Route path="/leaderboard" element={<LeaderboardPage user={user} />} />
            <Route path="/chat" element={<ChatPage user={user} />} />
            <Route path="/firebase-test" element={<FirebaseTestPage />} />
            <Route path="/profile" element={<ProfilePage user={user} />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/login" element={<LoginPage authForm={authForm} handleAuthChange={handleAuthChange} handleLogin={handleLogin} switchToRegister={switchToRegister} error={error} />} />
            <Route path="/register" element={<RegisterPage authForm={authForm} handleAuthChange={handleAuthChange} handleRegister={handleRegister} switchToLogin={switchToLogin} error={error} />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/contact" element={<ContactPage />} />
          </Routes>
          
          {(location.pathname === "/" || location.pathname === "/privacy" || location.pathname === "/terms" || location.pathname === "/contact") && <Footer />}
        </main>
        
        <RewardPopup reward={reward} onClose={() => setReward(null)} />
      </div>
    </div>
  );
}

export default App;
